import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Select from 'src/components/common/gridWrapper/Grid_Select';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { CommonCodeColumn, CompCdColumn, WorkPlaceColumn } from 'src/utils/grid/GridColumnContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';
import { defaultTQC2010_R01HeaderDto, GridRowData, TQC2010_R01HeaderDto } from './Dto_TQC2010_R01';
import { useTQC2010_R01Store } from './Store_TQC2010_R01';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'TQC2010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC2010_R01HeaderDto> | null>;
	onRowClick?: (row: TQC2010_R01HeaderDto) => void;
}


const headerNames: Record<string, string> = {
    rowIndex: '',
};


export const Table_TQC2010_R01 = ({ gridRef, onRowClick }: Props) => {
	const {
		TQC2010_R01List,
		selectedTQC2010_R01,
		initSelectedTQC2010_R01,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useTQC2010_R01Store();

	const {
		jobTeamList,
		compCdList,
		workPlaceList,
		workProcessList,
	} = useCommonCodeStore();

	const {
		getRowId,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: TQC2010_R01List || [],
		defaultGridData: defaultTQC2010_R01HeaderDto as GridRowData,
		selectedRow: selectedTQC2010_R01,
	});

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const selectedValueIndexList = useMemo(
		() => (selectedTQC2010_R01?.valueIndex != null ? [String(selectedTQC2010_R01.valueIndex)] : []),
		[selectedTQC2010_R01?.valueIndex],
	);

	const getCellStyle = useCallback((params: any, key: string) => onlyTouchGridStyle(key, 'productWorkNo')(params), []);

	const baseColumnDefs: ColDef<TQC2010_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '공정배정No',
			colId: 'productWorkNo',
			field: 'productWorkNo',
			width: 150,
			editable: false,
			cellStyle: (params) => getCellStyle(params, 'productWorkNo'),
		},
		{
			headerName: '사업장',
			colId: 'compCd',
			field: 'compCd',
			width: 100,
			cellStyle: (params) => getCellStyle(params, 'compCd'),
			...CompCdColumn.options(compCdList, false, undefined, undefined),
		},
		{
			headerName: '작업일자',
			colId: 'workDt',
			field: 'workDt',
			width: 100,
			editable: false,
			cellStyle: (params) => getCellStyle(params, 'workDt'),
			valueFormatter: (params) => (params.data?.workDt ? formatDate(new Date(params.data.workDt)) : ''),
			comparator: cellDateComparator,
		},
		{
			headerName: '작업공정',
			colId: 'workplaceCd',
			field: 'workplaceCd',
			width: 100,
			cellStyle: (params) => getCellStyle(params, 'workplaceCd'),
			...WorkPlaceColumn.options(
				workPlaceList.filter((opt) => opt.useYn === 'Y' && opt.orderYn === 'Y'),
				false,
				(params) => params.data?.compCd,
				undefined,
				() => {},
			),
		},
		{
			headerName: '공정유형',
			colId: 'workProcess',
			field: 'workProcess',
			width: 100,
			cellStyle: (params) => getCellStyle(params, 'workProcess'),
			...CommonCodeColumn.options(workProcessList, false, undefined, undefined),
		},
		{
			headerName: '작업팀',
			colId: 'jobTeam',
			field: 'jobTeam',
			width: 100,
			cellStyle: (params) => getCellStyle(params, 'jobTeam'),
			...CommonCodeColumn.options(jobTeamList, false, undefined, undefined),
		},
		{
			headerName: '작업자',
			colId: 'workEmp',
			field: 'workEmp',
			width: 100,
			editable: false,
			cellStyle: (params) => getCellStyle(params, 'workEmp'),
		},
		{
			headerName: '작업자명',
			colId: 'workEmpNm',
			field: 'workEmpNm',
			width: 100,
			editable: false,
			cellStyle: (params) => getCellStyle(params, 'workEmpNm'),
		},
		{
			headerName: '품목코드',
			colId: 'itemCd',
			field: 'itemCd',
			width: 150,
			editable: false,
			cellStyle: (params) => getCellStyle(params, 'itemCd'),
		},
		{
			headerName: '품명',
			colId: 'itemNm',
			field: 'itemNm',
			width: 200,
			editable: false,
			cellStyle: (params) => getCellStyle(params, 'itemNm'),
		},
		{
			headerName: '규격',
			colId: 'itemSpec',
			field: 'itemSpec',
			width: 200,
			editable: false,
			cellStyle: (params) => getCellStyle(params, 'itemSpec'),
		},
		{
			headerName: '지시량',
			colId: 'orderQty',
			field: 'orderQty',
			width: 100,
			editable: false,
			cellStyle: (params) => getCellStyle(params, 'orderQty'),
			valueFormatter: (params) => (params.value != null ? params.value.toLocaleString() : ''),
		},
		{
			headerName: '검사구분',
			colId: 'inspectTypeNm',
			field: 'inspectTypeNm',
			width: 100,
			editable: false,
			cellStyle: (params) => getCellStyle(params, 'inspectTypeNm'),
		},
	], [
		compCdList,
		jobTeamList,
		workPlaceList,
		workProcessList,
		getCellStyle,
		cellDateComparator,
	]);

	const columnDefs: ColDef<GridRowData>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<GridRowData>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState]);

	const getRowIdForGrid = getRowId;

	const handleRowClicked = useCallback((e: RowClickedEvent<TQC2010_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		if (!e.data) return;
		onRowClick?.(e.data);
	}, [onRowClick]);

	const rowClassRules = useMemo(() => ({
		selected: (params: any) => {
			if (!params.data || selectedTQC2010_R01?.valueIndex == null) return false;
			return params.data.valueIndex === selectedTQC2010_R01.valueIndex;
		},
	}), [selectedTQC2010_R01?.valueIndex]);

	const pinnedBottomRow = useMemo(() => [{
		rowIndex: '',
		productWorkNo: `${TQC2010_R01List.length.toLocaleString()}건`,
		editable: false,
	}], [TQC2010_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [TQC2010_R01List, selectedTQC2010_R01]);

	return (
		<div className="flex flex-col gap-3 h-full min-h-0">
			<GridContextWrapper<GridRowData>
				enableCellCalculation
				gridRef={gridRef}
				paddedGridData={TQC2010_R01List}
				initSelected={initSelectedTQC2010_R01}
				defaultRowData={defaultTQC2010_R01HeaderDto}
				baseColumnDefs={columnDefs}
				setColumnState={setColumnState}
				localStorageKey={COLUMN_STATE_KEY}
			>
				<Grid_Select<GridRowData>
					ref={gridRef}
					rowData={TQC2010_R01List}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					getRowId={getRowIdForGrid}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
					onRowClicked={handleRowClicked}
					pinnedBottomRowData={pinnedBottomRow}
					rowClassRules={rowClassRules}
					isRowSelectable={(node) => !!node.data}
					suppressRowClickSelection={false}
					ensureDomOrder={true}
					scrollTop={scrollTop}
					setScrollTop={setScrollTop}
				/>
			</GridContextWrapper>
		</div>
	);
};
